import type { ApproveResponse, SignupDto } from '@aawas/contracts';
import { Building2, Mail, MessageCircle, Phone } from 'lucide-react';
import { useState } from 'react';
import { copyText } from '../lib/clipboard';
import { useApproveSignup, usePlatformSignups, useReissueSignupCode, useRejectSignup } from '../lib/queries';
import { Badge, Button, Card, Empty, ErrorNote, Field, PageHeader, Segmented, Sheet, Spinner, Textarea, timeAgo } from '../ui';
import { useToast } from '../ui/toast';

type Tab = 'pending' | 'approved' | 'rejected';

/** The message the operator sends to a new admin (WhatsApp), with the site address and their one-time code. */
const welcomeMessage = (r: ApproveResponse) =>
  `Welcome to Expansio Aawas! Your society is set up.\n\nOpen ${window.location.origin}, enter your mobile number (${r.adminPhone.replace('+91', '')}) and this one-time code: ${r.code}\n\nThe code works once and is valid for 24 hours. After signing in, add your flats and residents under People.`;

/** Platform operator only: review requests from societies that want to join, and start them off. */
export function PlatformScreen() {
  const [tab, setTab] = useState<Tab>('pending');
  const q = usePlatformSignups(tab);
  const [approving, setApproving] = useState<SignupDto | null>(null);
  const [rejecting, setRejecting] = useState<SignupDto | null>(null);
  const [issued, setIssued] = useState<ApproveResponse | null>(null);
  const reissue = useReissueSignupCode();
  const toast = useToast();

  return (
    <div className="space-y-4">
      <PageHeader title="Society requests" subtitle="Societies that asked to join Expansio Aawas" />
      <Segmented
        value={tab}
        onChange={setTab}
        options={[
          { value: 'pending', label: 'Waiting' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
        ]}
      />

      {q.isLoading ? (
        <Spinner />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => q.refetch()} />
      ) : (q.data ?? []).length === 0 ? (
        <Empty
          icon={<Building2 className="h-8 w-8" />}
          title={tab === 'pending' ? 'No requests waiting' : tab === 'approved' ? 'No approved societies yet' : 'Nothing rejected'}
          hint={tab === 'pending' ? 'New requests from the Register your society page appear here.' : undefined}
        />
      ) : (
        <div className="space-y-3">
          {q.data!.map((r) => (
            <Card key={r.id} className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-bold">{r.societyName}</h2>
                  <p className="text-sm text-slate-600">
                    {r.city}
                    {r.flatsEstimate ? ` · about ${r.flatsEstimate} flats` : ''}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <Badge tone={r.status === 'pending' ? 'amber' : r.status === 'approved' ? 'green' : 'gray'}>{r.status === 'pending' ? 'Waiting' : r.status === 'approved' ? 'Approved' : 'Rejected'}</Badge>
                  <div className="mt-1 text-xs text-slate-500">{timeAgo(r.createdAt)}</div>
                </div>
              </div>

              <div className="space-y-1 text-sm">
                <div className="font-medium">{r.adminName}</div>
                <a href={`tel:${r.adminPhone}`} className="flex items-center gap-2 text-brand-700"><Phone className="h-4 w-4" aria-hidden />{r.adminPhone}</a>
                {r.adminEmail && <a href={`mailto:${r.adminEmail}`} className="flex items-center gap-2 text-brand-700"><Mail className="h-4 w-4" aria-hidden />{r.adminEmail}</a>}
              </div>
              {r.address && <p className="text-sm text-slate-700">{r.address}</p>}
              {r.message && <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">{r.message}</p>}
              {r.decisionNote && <p className="text-sm text-slate-600">Note: {r.decisionNote}</p>}

              {r.status === 'pending' && (
                <div className="flex gap-2">
                  <Button variant="secondary" className="flex-1" onClick={() => setRejecting(r)}>Reject</Button>
                  <Button variant="success" className="flex-1" onClick={() => setApproving(r)}>Approve</Button>
                </div>
              )}
              {r.status === 'approved' && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={reissue.isPending && reissue.variables === r.id}
                  onClick={() => reissue.mutate(r.id, { onSuccess: (x) => setIssued(x), onError: (e) => toast.error(e) })}
                >
                  New login code
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      <ApproveSheet request={approving} onClose={() => setApproving(null)} onDone={(x) => { setApproving(null); setIssued(x); }} />
      <RejectSheet request={rejecting} onClose={() => setRejecting(null)} />
      <CodeSheet issued={issued} onClose={() => setIssued(null)} />
    </div>
  );
}

function ApproveSheet({ request, onClose, onDone }: { request: SignupDto | null; onClose: () => void; onDone: (r: ApproveResponse) => void }) {
  const approve = useApproveSignup();
  const toast = useToast();
  return (
    <Sheet open={!!request} onClose={onClose} title="Approve this society?">
      {request && (
        <div className="space-y-4">
          <p className="text-slate-700">
            This creates <b>{request.societyName}</b> ({request.city}) and makes <b>{request.adminName}</b> ({request.adminPhone}) its admin. You will then get a one-time login code to send them.
          </p>
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">Check that this really is the person and society before you approve. Whoever receives the code becomes the society's admin.</p>
          <Button size="lg" className="w-full" variant="success" loading={approve.isPending} onClick={() => approve.mutate(request.id, { onSuccess: onDone, onError: (e) => toast.error(e) })}>
            Create society
          </Button>
        </div>
      )}
    </Sheet>
  );
}

function RejectSheet({ request, onClose }: { request: SignupDto | null; onClose: () => void }) {
  const reject = useRejectSignup();
  const toast = useToast();
  const [note, setNote] = useState('');
  return (
    <Sheet open={!!request} onClose={() => { setNote(''); onClose(); }} title="Reject this request?">
      {request && (
        <div className="space-y-4">
          <p className="text-slate-700">Nothing is created. <b>{request.adminName}</b> can send a new request later.</p>
          <Field label="Reason (optional, only you see it)">
            <Textarea rows={3} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <Button
            size="lg"
            className="w-full"
            variant="danger"
            loading={reject.isPending}
            onClick={() => reject.mutate({ id: request.id, ...(note.trim() && { note: note.trim() }) }, { onSuccess: () => { toast.success('Rejected'); setNote(''); onClose(); }, onError: (e) => toast.error(e) })}
          >
            Reject request
          </Button>
        </div>
      )}
    </Sheet>
  );
}

function CodeSheet({ issued, onClose }: { issued: ApproveResponse | null; onClose: () => void }) {
  const toast = useToast();
  const message = issued ? welcomeMessage(issued) : '';
  const wa = issued ? `https://wa.me/${issued.adminPhone.replace('+', '')}?text=${encodeURIComponent(message)}` : '#';
  return (
    <Sheet open={!!issued} onClose={onClose} title="Send this to the new admin">
      {issued && (
        <div className="space-y-4 text-center">
          <p className="text-slate-700">
            Login code for <b>{issued.adminName}</b> ({issued.adminPhone}). It works once and expires in 24 hours.
          </p>
          <div className="select-all rounded-2xl bg-brand-50 py-5 font-mono text-5xl font-bold tracking-[0.3em] text-brand-700" aria-label={`Login code ${issued.code.split('').join(' ')}`}>{issued.code}</div>
          <a href={wa} target="_blank" rel="noreferrer" className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 font-semibold text-white active:bg-emerald-700">
            <MessageCircle className="h-5 w-5" aria-hidden /> Send on WhatsApp
          </a>
          <Button variant="secondary" size="lg" className="w-full" onClick={async () => { (await copyText(message)) ? toast.success('Message copied') : toast.show({ title: 'Could not copy automatically', body: `Send them this code: ${issued.code}`, tone: 'alert' }); }}>
            Copy the message
          </Button>
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">This code is shown only now. Use <b>New login code</b> on the Approved tab if you need another; that cancels this one.</p>
        </div>
      )}
    </Sheet>
  );
}
